import { useSelector, useDispatch } from 'react-redux';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthHero from '../../components/auth/AuthHero';
import AccountTypeStep from '../../components/auth/AccountTypeStep';
import ClubDetailsStep from '../../components/auth/ClubDetailsStep';
import AgencyDetailsStep from '../../components/auth/AgencyDetailsStep';
import AgentDetailsStep from '../../components/auth/AgentDetailsStep';
import LawyerDetailsStep from '../../components/auth/LawyerDetailsStep';
import CoachDetailsStep from '../../components/auth/CoachDetailsStep';
import PlayerDetailsStep from '../../components/auth/PlayerDetailsStep';
import StaffDetailsStep from '../../components/auth/StaffDetailsStep';
import OrganizerDetailsStep from '../../components/auth/OrganizerDetailsStep';
import SuccessStep from '../../components/auth/SuccessStep';
import { setVerificationStep, setSignupData } from '../../store/slices/signupSlice';

const Onboarding = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { verificationStep, accountType, invitationContext } = useSelector(state => state.signup);
  const auth = useSelector(state => state.auth);

  // Check if user has roles assigned (indicating completed onboarding)
  const hasRoles = auth.user && auth.user.roles && auth.user.roles.length > 0;

  console.log('Onboarding render:', { 
    hasRoles, 
    accountType, 
    verificationStep, 
    isInvitation: invitationContext.isInvitationFlow,
    invitationType: invitationContext.type 
  });

  // Handle redirects and invitation flow
  useEffect(() => {
    console.log('Onboarding useEffect triggered');
    
    // If user has roles, they've completed onboarding - redirect to home
    if (hasRoles) {
      console.log('User has roles, redirecting to home');
      navigate('/');
      return;
    }

    // Handle invitation flow - auto-set account type and go to details step
    // EXCEPT for players with wyscout_id who are auto-created in PasswordStep
    if (invitationContext.isInvitationFlow && invitationContext.type && !accountType) {
      console.log('Setting up invitation flow for:', invitationContext.type);
      const invitationType = invitationContext.type;
      
      // Map invitation type to account type
      let mappedAccountType = invitationType;
      if (invitationType === 'player') {
        mappedAccountType = 'player';
      } else if (invitationType === 'agent') {
        mappedAccountType = 'agent';
      } else if (invitationType === 'staff') {
        mappedAccountType = 'staff';
      } else if (invitationType === 'coach') {
        mappedAccountType = 'coach';
      }
      
      // Set account type and go to details step
      // (Players with wyscout_id are handled in PasswordStep and never reach here)
      dispatch(setSignupData({ accountType: mappedAccountType }));
      dispatch(setVerificationStep('details'));
      console.log('Set accountType to:', mappedAccountType, 'and verificationStep to: details');
      return;
    }

    // Normal flow - set to 'type' if we don't have an account type selected
    if (!accountType && verificationStep !== 'type' && !invitationContext.isInvitationFlow) {
      console.log('Setting up normal flow - account type selection');
      dispatch(setVerificationStep('type'));
    }
  }, [hasRoles, accountType, verificationStep, invitationContext, dispatch, navigate]);

  // Don't render anything if user has roles (will redirect)
  if (hasRoles) {
    return null;
  }

  // Handle completion step
  if (verificationStep === 'complete') {
    return <SuccessStep />;
  }

  const renderDetailsStep = () => {
    console.log('Rendering details step for accountType:', accountType);
    switch (accountType) {
      case 'club':
        return <ClubDetailsStep />;
      case 'agency':
        return <AgencyDetailsStep />;
      case 'agent':
        return <AgentDetailsStep />;
      case 'lawyer':
        return <LawyerDetailsStep />;
      case 'coach':
        return <CoachDetailsStep />;
      case 'player':
        return <PlayerDetailsStep />;
      case 'staff':
        return <StaffDetailsStep />;
      case 'organizer':
        return <OrganizerDetailsStep />;
      default:
        return <AccountTypeStep />;
    }
  };

  const renderContent = () => {
    // For invitation flow, skip role selection and go directly to details
    if (invitationContext.isInvitationFlow && accountType) {
      console.log('Invitation flow detected - rendering details step');
      return renderDetailsStep();
    }
    
    // If details step and account type selected -> show details
    // Otherwise -> show account type selection
    if (verificationStep === 'details' && accountType) {
      console.log('Details step with accountType - rendering details');
      return renderDetailsStep();
    } else {
      console.log('Rendering account type selection');
      return <AccountTypeStep />;
    }
  };

  // Early return for invitation flows to prevent flash of AccountTypeStep
  if (invitationContext.isInvitationFlow && !accountType) {
    console.log('Invitation flow loading - showing loading state');
    return (
      <div className="flex min-h-screen">
        <AuthHero pageName={"onboarding"}/>
        <div className="flex-1 bg-white flex items-center justify-center">
          <div className="w-full max-w-md px-6">
            <div className="text-center">
              <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2" />
              <p className="text-gray-500">Setting up your account...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      <AuthHero pageName={"onboarding"}/>
      <div className="flex-1 bg-white flex items-center justify-center">
        <div className="w-full max-w-xl px-5">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default Onboarding; 